import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useAuthOptional } from '@/context/AuthContext';
import { toast } from '@/hooks/use-toast';
import { normalizeRole } from '@/lib/roles';
import {
  ACCOUNT_DEFAULTS,
  type AccountRecord,
  type AccountRole,
  type AccountTeamMember,
} from './accountFixture';
import {
  useAccountSource,
  mergeAccountRecord,
  type AccountBilling,
  type AccountFacts,
  type AccountUnboundField,
  type AccountViewer,
} from './accountSource';
import { useSettingsSource, type SettingsSites } from '@/pages/settings/settingsSource';
import {
  ACCOUNT_CARDS,
  ACCOUNT_TAXONOMY_BY_PATH,
  accountCardSlices,
  accountGet,
  accountPut,
  isPublicCard,
  type AccountCardKey,
} from './accountModel';

/**
 * AR-380 — the Account center's state.
 *
 * One record, one set of open cards, one draft copy per open slice. Cancel
 * discards by throwing the copy away; Save writes only the card's own fields
 * back, so two cards on one slice never save each other's work.
 *
 * Edits persist to localStorage so a reviewer's walkthrough survives a reload —
 * the record is what is being reviewed, and AR-381 swaps the source here
 * without any page changing.
 */

/**
 * The record is persisted per user. It is sample data, but a member editing it
 * types their own details in, and the browser may be shared — so one member's
 * record must never load for the next. The un-namespaced key this started with
 * is removed on sight for the same reason.
 */
const STORAGE_PREFIX = 'aivia-account-record';
const LEGACY_STORAGE_KEY = STORAGE_PREFIX;
const storageKeyFor = (userId?: number | string | null) =>
  // Never the bare prefix: that is the legacy key this provider deletes on
  // mount, so falling back to it would wipe the record it had just read.
  `${STORAGE_PREFIX}:${userId ?? 'anonymous'}`;

type Draft = Partial<Record<keyof AccountRecord, Record<string, unknown>>>;

// ---------------------------------------------------------------------------
// The settings search's one-shot signal
// ---------------------------------------------------------------------------

/**
 * The card a settings result asked to open, held BESIDE the React tree.
 *
 * A result names a field on a page the reader is not on yet, so the click
 * navigates and then the card is supposed to scroll into view and flash. The
 * two halves of that are in different mount generations: each page of a Level
 * 2 module is its own `<Route>` wrapping its own layout, so wouter's `<Switch>`
 * tears the whole shell down — this provider with it — and builds a new one
 * before the target card exists. React state cannot cross that gap, and a
 * signal kept only in state was simply discarded: the reader landed on the
 * right page with nothing highlighted.
 *
 * So the request lives here, outside the tree, and the next provider to mount
 * TAKES it. Reading clears it, and the card clears it again when it answers,
 * so one click can only ever flash one card once — a stale request cannot sit
 * here waiting to fire at whoever opens the module next.
 */
let pendingFocusCard: string | null = null;

/** Read the pending request and clear it. */
function takePendingFocusCard(): string | null {
  const card = pendingFocusCard;
  pendingFocusCard = null;
  return card;
}

/** A confirmation waiting on the reader's answer. */
export type AccountConfirm =
  | { kind: 'memory' }
  | { kind: 'unsaved'; card: AccountCardKey }
  | { kind: 'unsavedAll' }
  | { kind: 'deactivate'; index: number }
  | null;

export interface AccountRecordValue {
  record: AccountRecord;
  role: AccountRole;
  /** The facts the pages state but do not edit (AR-381). */
  facts: AccountFacts;
  /** Account center fields with no source in the shipped schema (AR-381). */
  unbound: AccountUnboundField[];
  /**
   * Who is asking, from the data. Null until the migrated model answers, in
   * which case the auth context's role stands.
   */
  viewer: AccountViewer | null;
  /**
   * Plan, price, renewal and every charge, from Stripe. Null for a non-owner
   * and when Stripe is not configured (D-ACCT-40 / D-ACCT-42).
   */
  billing: AccountBilling | null;
  /** True once the pages show the migrated model rather than the sample. */
  isLive: boolean;
  /**
   * AR-391 — the Settings module's half of the record. Its own flag, its own
   * payload: the AI writing profile is bound here, and the Sites account
   * rides beside the record because it is a fact the page states, not a
   * slice it edits. Null until the module's flags are on and the model answers.
   */
  sites: SettingsSites | null;
  /** Settings module fields with no source in the shipped schema (AR-391). */
  settingsUnbound: AccountUnboundField[];
  /** True once the Settings pages show the member's own settings. */
  isSettingsLive: boolean;
  /** The owner at the head, then the rest of the team. Every count derives from this. */
  members: (AccountTeamMember & { name: string; brand: string; you: boolean; index: number })[];

  // --- the editing model -------------------------------------------------
  openCards: ReadonlySet<AccountCardKey>;
  isOpen: (card: AccountCardKey) => boolean;
  /** The card's live draft value at a dot path ("you.first"). */
  draftValue: (path: string) => unknown;
  setDraftValue: (path: string, value: unknown) => void;
  openCard: (card: AccountCardKey) => void;
  saveCard: (card: AccountCardKey) => void;
  /** Cancel. Asks first when the draft has moved away from the record. */
  cancelCard: (card: AccountCardKey) => void;
  discardCard: (card: AccountCardKey) => void;
  isDirty: (card: AccountCardKey) => boolean;
  anyDirty: () => boolean;
  saveAll: () => void;
  cancelAll: () => void;
  discardAll: () => void;

  // --- switches and selects that write on click --------------------------
  setLive: (path: string, value: unknown) => void;

  // --- list (chip) fields -------------------------------------------------
  addTag: (path: string, typed: string) => void;
  removeTag: (path: string, index: number) => void;
  tagQuery: Record<string, string>;
  setTagQuery: (path: string, value: string) => void;
  /** The path whose last Add was refused because the taxonomy does not carry it. */
  tagError: string | null;

  // --- public profile publishing -----------------------------------------
  publish: () => void;

  // --- team ---------------------------------------------------------------
  setMemberRole: (index: number, role: AccountRole) => void;
  /**
   * Deactivate or reactivate a member. `confirmed` is the Manage pop-up's
   * SECOND step and only that step may deactivate (D-ACCT-46) — the server
   * refuses a deactivation without it, so the rule holds even if a future
   * caller forgets the pop-up.
   */
  setMemberStatus: (
    index: number,
    status: 'Active' | 'Deactivated',
    confirmed?: boolean,
  ) => void;

  // --- writing samples ----------------------------------------------------
  addWritingSample: (kind: string, title: string, words: number) => void;
  removeWritingSample: (index: number) => void;

  // --- one-off panels -----------------------------------------------------
  confirm: AccountConfirm;
  setConfirm: (c: AccountConfirm) => void;

  /**
   * The card a search result named. The page scrolls to it and marks it for a
   * moment so the eye finds it; the card clears the mark itself.
   */
  focusCard: string | null;
  setFocusCard: (card: string | null) => void;

  /** Put the sample record back the way it shipped. */
  reset: () => void;
}

/**
 * The account role of whoever is signed in. The visibility rules are an
 * authorization decision, so they read THIS and never the sample record — that
 * is editable from the console and defaults to Owner. An unrecognised or
 * missing role gets the least privilege, not the most.
 */
export function accountRoleForUser(appRole?: string | null): AccountRole {
  switch (normalizeRole(appRole)) {
    case 'AGENCY_OWNER':
    // An admin already reaches everything in the app; the account center is
    // not the surface that should tell them otherwise.
    case 'SYSTEM_ADMIN':
    case 'ADMIN':
      return 'Owner';
    case 'AGENCY_MANAGER':
      return 'Manager';
    default:
      return 'Associate Advisor';
  }
}

const AccountRecordContext = createContext<AccountRecordValue | null>(null);

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

function readStored(key: string): AccountRecord {
  const base = clone(ACCOUNT_DEFAULTS);
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const stored = JSON.parse(raw) as Partial<AccountRecord>;
      // Merged one card deep: a stored slice replaces its default wholesale, so
      // a field added to a default later is not shadowed by a stale slice.
      (Object.keys(base) as (keyof AccountRecord)[]).forEach((k) => {
        if (stored && stored[k]) Object.assign(base[k] as object, stored[k] as object);
      });
    }
  } catch {
    // A corrupt key is not worth a broken page — the defaults stand.
  }
  return base;
}

export const AccountRecordProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = useAuthOptional();
  const role = accountRoleForUser(auth?.user?.role);
  const storageKey = storageKeyFor(auth?.user?.id);
  const userId = auth?.user?.id ?? null;
  const queryClient = useQueryClient();
  /**
   * The record and the key it was loaded from move together. Kept apart, they
   * drift: the id arrives a render after the provider mounts, and a persist
   * effect firing on the key change would write the PREVIOUS record into the
   * new user's key — overwriting their record with someone else's, which is
   * the very thing scoping the key is meant to prevent.
   */
  const [loaded, setLoaded] = useState<{ key: string; record: AccountRecord }>(() => ({
    key: storageKey,
    record: readStored(storageKey),
  }));
  const scratch = loaded.record;
  const setRecord = useCallback(
    (next: AccountRecord | ((prev: AccountRecord) => AccountRecord)) => {
      setLoaded((prev) => ({
        key: prev.key,
        record: typeof next === 'function' ? (next as (p: AccountRecord) => AccountRecord)(prev.record) : next,
      }));
    },
    [],
  );
  const [openCards, setOpenCards] = useState<Set<AccountCardKey>>(() => new Set());
  const [confirm, setConfirm] = useState<AccountConfirm>(null);
  const [tagQuery, setTagQueryState] = useState<Record<string, string>>({});
  const [tagError, setTagError] = useState<string | null>(null);
  const [focusCard, setFocusCardState] = useState<string | null>(null);

  /**
   * Both halves of the signal. The setter records the request outside the tree
   * as well as in state, so it survives the shell being rebuilt; the effect
   * picks up a request made by the shell that has just been torn down.
   */
  const setFocusCard = useCallback((next: string | null) => {
    pendingFocusCard = next;
    setFocusCardState(next);
  }, []);

  useEffect(() => {
    const requested = takePendingFocusCard();
    if (requested) setFocusCardState(requested);
  }, []);
  // The drafts are a ref, not state: typing must not re-render the page or the
  // caret jumps. A structural change (a checkbox that changes which fields show,
  // a chip added) bumps `draftTick` to repaint.
  const drafts = useRef<Draft>({});
  const [, setDraftTick] = useState(0);
  const repaint = useCallback(() => setDraftTick((n) => n + 1), []);

  // AR-381 — the migrated model, when both migration flags are on.
  const { payload, isLive } = useAccountSource();
  const facts = useMemo<AccountFacts>(() => payload?.facts ?? {}, [payload]);
  const unbound = useMemo(() => payload?.unbound ?? [], [payload]);
  const viewer = payload?.viewer ?? null;
  const billing = payload?.billing ?? null;

  // AR-391 — the Settings module's slices, when the module's own flags are on.
  const { payload: settingsPayload, isLive: isSettingsLive } = useSettingsSource();
  const sites = settingsPayload?.sites ?? null;
  const settingsUnbound = useMemo(
    () => settingsPayload?.unbound ?? [],
    [settingsPayload],
  );

  /**
   * The record every page reads: the member's scratch copy with the migrated
   * model laid OVER it.
   *
   * Over, not under, and computed rather than stored. A bound field is the
   * model's to state, so a stale local edit must not outrank it — and because
   * the overlay never lands in `loaded.record`, the migrated values (a real
   * name, a real email, a real address) are never written to localStorage.
   * AR-381 does not write account fields back, so a bound field is read-only
   * and says so by snapping back; an unbound field is the member's and
   * persists.
   *
   * The Settings module's payload is laid over the same way (AR-391). The two
   * bind disjoint slices, so the order between them does not matter.
   */
  const record = useMemo(
    () =>
      mergeAccountRecord(
        mergeAccountRecord(scratch, payload?.record),
        settingsPayload?.record,
      ),
    [scratch, payload, settingsPayload],
  );

  // Drop the shared key an earlier build wrote: it is not this user's to read.
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // Nothing to clear is not a failure.
    }
  }, []);

  // A different member signing in on this browser starts from their own record,
  // never the last one's — and any half-finished edit goes with it.
  useEffect(() => {
    if (loaded.key === storageKey) return;
    drafts.current = {};
    setOpenCards(new Set());
    setConfirm(null);
    setLoaded({ key: storageKey, record: readStored(storageKey) });
  }, [loaded.key, storageKey]);

  // The record is persisted from an effect, never from inside the state
  // updater: an updater must be pure, and React may run it twice. It writes
  // only once the record and the key agree — otherwise the render in which the
  // key changes would persist the last user's record under the new key.
  const firstRender = useRef(true);
  useEffect(() => {
    if (loaded.key !== storageKey) return;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    try {
      localStorage.setItem(loaded.key, JSON.stringify(loaded.record));
    } catch {
      // Storage being unavailable is not a reason to lose the edit on screen.
    }
  }, [loaded, storageKey]);

  const commit = useCallback((mutate: (draft: AccountRecord) => void) => {
    setRecord((prev) => {
      const next = clone(prev);
      mutate(next);
      return next;
    });
  }, []);

  // --- drafts -------------------------------------------------------------

  const ensureSlices = useCallback(
    (card: AccountCardKey, from: AccountRecord) => {
      accountCardSlices(card).forEach((slice) => {
        if (!drafts.current[slice]) {
          drafts.current[slice] = clone(from[slice]) as Record<string, unknown>;
        }
      });
    },
    [],
  );

  const dropSlices = useCallback((card: AccountCardKey, stillOpen: Set<AccountCardKey>) => {
    accountCardSlices(card).forEach((slice) => {
      const heldByAnother = [...stillOpen].some((other) =>
        accountCardSlices(other).includes(slice),
      );
      if (!heldByAnother) delete drafts.current[slice];
    });
  }, []);

  const openCard = useCallback(
    (card: AccountCardKey) => {
      ensureSlices(card, record);
      setOpenCards((prev) => new Set(prev).add(card));
    },
    [ensureSlices, record],
  );

  const draftValue = useCallback((path: string) => accountGet(drafts.current, path), []);

  const setDraftValue = useCallback(
    (path: string, value: unknown) => {
      accountPut(drafts.current, path, value);
      // No repaint: a controlled re-render on every keystroke moves the caret.
    },
    [],
  );

  const isDirty = useCallback(
    (card: AccountCardKey) => {
      const spec = ACCOUNT_CARDS[card] as {
        slice: keyof AccountRecord;
        fields?: readonly string[];
        also?: Record<string, readonly string[]>;
      };
      const draft = drafts.current[spec.slice];
      const fields = spec.fields ?? Object.keys(record[spec.slice] as object);
      if (
        draft &&
        fields.some(
          (f) =>
            JSON.stringify(draft[f]) !==
            JSON.stringify((record[spec.slice] as Record<string, unknown>)[f]),
        )
      ) {
        return true;
      }
      return Object.entries(spec.also ?? {}).some(([slice, fs]) => {
        const other = drafts.current[slice as keyof AccountRecord];
        return (
          other &&
          fs.some(
            (f) =>
              JSON.stringify(other[f]) !==
              JSON.stringify(
                (record[slice as keyof AccountRecord] as Record<string, unknown>)[f],
              ),
          )
        );
      });
    },
    [record],
  );

  const closeCard = useCallback(
    (card: AccountCardKey) => {
      setOpenCards((prev) => {
        const next = new Set(prev);
        next.delete(card);
        dropSlices(card, next);
        return next;
      });
    },
    [dropSlices],
  );

  /**
   * Write one card's own fields into `target`, reading from `snapshot` rather
   * than the live `drafts` ref. The ref is cleared synchronously by the caller,
   * but React runs the state updater later — reading the ref here would find it
   * already empty and silently discard the edit.
   */
  const writeCard = useCallback(
    (card: AccountCardKey, target: AccountRecord, snapshot: Draft) => {
      const spec = ACCOUNT_CARDS[card] as {
        slice: keyof AccountRecord;
        fields?: readonly string[];
        also?: Record<string, readonly string[]>;
      };
      const draft = snapshot[spec.slice];
      if (!draft) return;
      /**
       * Write only what the member actually CHANGED.
       *
       * The form opens on the displayed record, so an untouched field's draft
       * value is whatever the migrated model answered. Copying the whole card
       * back would put the member's real name, email and address into the
       * scratch copy, which is the one thing persisted to localStorage.
       */
      const changed = (slice: keyof AccountRecord, f: string, value: unknown) =>
        JSON.stringify(value) !==
        JSON.stringify((record[slice] as Record<string, unknown>)[f]);

      const fields = spec.fields ?? Object.keys(target[spec.slice] as object);
      fields.forEach((f) => {
        if (!changed(spec.slice, f, draft[f])) return;
        (target[spec.slice] as Record<string, unknown>)[f] = clone(draft[f]);
      });
      Object.entries(spec.also ?? {}).forEach(([slice, fs]) => {
        const other = snapshot[slice as keyof AccountRecord];
        if (!other) return;
        fs.forEach((f) => {
          if (!changed(slice as keyof AccountRecord, f, other[f])) return;
          (target[slice as keyof AccountRecord] as Record<string, unknown>)[f] = clone(
            other[f],
          );
        });
      });
      // A public card saves a draft; Publish changes is what makes it live.
      if (isPublicCard(card)) target.pubmeta.dirty = true;
    },
    [record],
  );

  const saveCard = useCallback(
    (card: AccountCardKey) => {
      const snapshot = clone(drafts.current);
      commit((next) => writeCard(card, next, snapshot));
      closeCard(card);
      setConfirm(null);
    },
    [closeCard, commit, writeCard],
  );

  const discardCard = useCallback(
    (card: AccountCardKey) => {
      accountCardSlices(card).forEach((slice) => {
        delete drafts.current[slice];
      });
      closeCard(card);
      setConfirm(null);
    },
    [closeCard],
  );

  /** Cancel with unsaved changes asks first; an untouched draft closes silently. */
  const cancelCard = useCallback(
    (card: AccountCardKey) => {
      if (isDirty(card)) setConfirm({ kind: 'unsaved', card });
      else discardCard(card);
    },
    [discardCard, isDirty],
  );

  const anyDirty = useCallback(
    () => [...openCards].some((c) => isDirty(c)),
    [isDirty, openCards],
  );

  const saveAll = useCallback(() => {
    const cards = [...openCards];
    const snapshot = clone(drafts.current);
    commit((next) => cards.forEach((c) => writeCard(c, next, snapshot)));
    drafts.current = {};
    setOpenCards(new Set());
    setConfirm(null);
  }, [commit, openCards, writeCard]);

  const discardAll = useCallback(() => {
    drafts.current = {};
    setOpenCards(new Set());
    setConfirm(null);
  }, []);

  const cancelAll = useCallback(() => {
    if (anyDirty()) setConfirm({ kind: 'unsavedAll' });
    else discardAll();
  }, [anyDirty, discardAll]);

  // --- switches, selects, lists -------------------------------------------

  const setLive = useCallback(
    (path: string, value: unknown) => {
      commit((next) => accountPut(next, path, value));
    },
    [commit],
  );

  const setTagQuery = useCallback((path: string, value: string) => {
    setTagQueryState((prev) => ({ ...prev, [path]: value }));
    setTagError(null);
  }, []);

  /**
   * One add path for the button and for Enter. A controlled field takes only a
   * value its taxonomy carries, matched without case and stored in the
   * taxonomy's spelling; anything else is refused and the typed text stays.
   */
  const addTag = useCallback(
    (path: string, typed: string) => {
      const value = typed.trim();
      if (!value) return;
      const list = accountGet(drafts.current, path) as string[] | undefined;
      if (!list) return;
      const taxonomy = ACCOUNT_TAXONOMY_BY_PATH[path];
      if (taxonomy) {
        const hit = taxonomy.find((x) => x.toLowerCase() === value.toLowerCase());
        if (!hit) {
          setTagQueryState((prev) => ({ ...prev, [path]: value }));
          setTagError(path);
          return;
        }
        if (!list.includes(hit)) list.push(hit);
      } else if (!list.includes(value)) {
        list.push(value);
      }
      setTagQueryState((prev) => ({ ...prev, [path]: '' }));
      setTagError(null);
      repaint();
    },
    [repaint],
  );

  const removeTag = useCallback(
    (path: string, index: number) => {
      const list = accountGet(drafts.current, path) as string[] | undefined;
      if (!list) return;
      list.splice(index, 1);
      repaint();
    },
    [repaint],
  );

  // --- publishing, team, samples ------------------------------------------

  const publish = useCallback(() => {
    commit((next) => {
      next.pubmeta.dirty = false;
      next.pubmeta.published = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    });
  }, [commit]);

  /**
   * The local role editor. `index` is a position in the DISPLAYED team, which
   * is the migrated one while the model is answering — the scratch copy still
   * holds the one-row sample, so writing into it by that index would change
   * the wrong person and show nothing, because the overlay replaces the list
   * wholesale. AR-381 binds no role write, so while the model owns the list
   * the control is left to the ticket that adds the endpoint.
   */
  const setMemberRole = useCallback(
    (index: number, role: AccountRole) => {
      if (isLive) return;
      commit((next) => {
        if (next.team[index]) next.team[index].role = role;
      });
    },
    [commit, isLive],
  );

  const setMemberStatus = useCallback(
    (index: number, status: 'Active' | 'Deactivated', confirmed = false) => {
      const member = record.team[index];
      // Same reason as `setMemberRole`: the sample list is not the displayed
      // one once the model answers, so the optimistic local write is for the
      // sample record only. The API call below is what changes a real
      // member's status, and the refetch after it reflects it.
      if (!isLive) {
        commit((next) => {
          if (next.team[index]) next.team[index].status = status;
        });
      }
      setConfirm(null);
      // AR-381 — the status is `users.account_status`. A member with no id is
      // the sample record, which has no row to write.
      if (!isLive || !member?.id) return;
      void fetch(`/api/account-center/team/${member.id}/status`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: status === 'Active' ? 'active' : 'deactivated',
          confirmed,
        }),
      })
        .then((res) => {
          if (!res.ok) throw new Error(`Status change failed (${res.status})`);
          return queryClient.invalidateQueries({ queryKey: ['/api/account-center', userId] });
        })
        .catch(() => {
          // The pop-up has already closed, so the failure is a toast rather
          // than a dialog the reader is stranded in.
          toast({
            title: 'Could not change the member’s status',
            description: 'Nothing was changed. Try again, or ask Member Success.',
            variant: 'destructive',
          });
        });
    },
    [commit, isLive, record, queryClient, userId],
  );

  /**
   * The sample-record editors for writing samples. While the Settings module
   * shows the member's own profile, the list is `user_profiles.sample_*` and
   * the overlay replaces it wholesale — a local write would change nothing on
   * screen. AR-391 binds no sample write, so the page hides the controls and
   * these stand down until the ticket that adds the endpoint.
   */
  const addWritingSample = useCallback(
    (kind: string, title: string, words: number) => {
      if (isSettingsLive) return;
      commit((next) => {
        next.voice.samples.push({
          kind,
          title,
          words,
          added: new Date().toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
        });
      });
    },
    [commit, isSettingsLive],
  );

  const removeWritingSample = useCallback(
    (index: number) => {
      if (isSettingsLive) return;
      commit((next) => {
        next.voice.samples.splice(index, 1);
      });
    },
    [commit, isSettingsLive],
  );

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // Nothing to clear is not a failure.
    }
    drafts.current = {};
    setOpenCards(new Set());
    setConfirm(null);
    setRecord(clone(ACCOUNT_DEFAULTS));
  }, [storageKey]);

  const members = useMemo(
    () => [
      {
        first: record.you.first,
        last: record.you.last,
        name: `${record.you.first} ${record.you.last}`,
        email: record.you.email,
        role,
        status: 'Active' as const,
        brand: record.biz.brand,
        seen: 'Today',
        you: true,
        index: -1,
      },
      ...record.team.map((m, i) => ({
        ...m,
        name: `${m.first} ${m.last}`,
        brand: record.biz.brand,
        you: false,
        index: i,
      })),
    ],
    [record, role],
  );

  const value = useMemo<AccountRecordValue>(
    () => ({
      record,
      role,
      facts,
      unbound,
      viewer,
      billing,
      isLive,
      sites,
      settingsUnbound,
      isSettingsLive,
      members,
      openCards,
      isOpen: (card) => openCards.has(card),
      draftValue,
      setDraftValue,
      openCard,
      saveCard,
      cancelCard,
      discardCard,
      isDirty,
      anyDirty,
      saveAll,
      cancelAll,
      discardAll,
      setLive,
      addTag,
      removeTag,
      tagQuery,
      setTagQuery,
      tagError,
      publish,
      setMemberRole,
      setMemberStatus,
      addWritingSample,
      removeWritingSample,
      confirm,
      setConfirm,
      focusCard,
      setFocusCard,
      reset,
    }),
    [
      addTag,
      addWritingSample,
      anyDirty,
      billing,
      cancelAll,
      cancelCard,
      confirm,
      discardAll,
      discardCard,
      draftValue,
      facts,
      focusCard,
      isDirty,
      isLive,
      isSettingsLive,
      members,
      openCard,
      openCards,
      publish,
      record,
      removeTag,
      removeWritingSample,
      reset,
      role,
      saveAll,
      saveCard,
      setDraftValue,
      setLive,
      setMemberRole,
      setMemberStatus,
      setTagQuery,
      settingsUnbound,
      sites,
      tagError,
      tagQuery,
      unbound,
      viewer,
    ],
  );

  return (
    <AccountRecordContext.Provider value={value}>{children}</AccountRecordContext.Provider>
  );
};

export function useAccountRecord(): AccountRecordValue {
  const ctx = useContext(AccountRecordContext);
  if (!ctx) {
    throw new Error('useAccountRecord must be used inside <AccountRecordProvider>');
  }
  return ctx;
}
